import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Snackbar,
  Typography,
  Alert,
  Switch,
  Tooltip,
  FormControlLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { Task, TaskStatus } from "../../models";
import { TaskService } from "../../services/coachingNavigator/taskService";
import { FreeSingleSelect } from "../freeSingleSelect";
import { PaperComponent } from "../../util/paperComponent";
import { Context } from "../context";
import ConfirmationPanel from "../confirmationPanel";
import config from "../../config";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (task: Task) => void;
  onSaved?: (task: Task) => void;
  defaultClientId?: string;
  defaultSessionId?: string;
  defaultSessionName?: string;
  defaultCategory?: string;
  defaultActorName?: string;
  defaultCreatorEmail?: string;
  initialTask?: Task | null;
};

const AddTask: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  onSaved,
  defaultClientId,
  defaultSessionId,
  defaultSessionName,
  defaultCategory = "Coaching Hub",
  defaultActorName,
  defaultCreatorEmail,
  initialTask,
}) => {
  const { t } = useTranslation();
  const appContext = React.useContext(Context);
  const isActor = appContext.state.invoker?.role === "actor";
  const taskService = useMemo(
    () => new TaskService(config.api.baseUrl, "/coachingtasks"),
    [],
  );

  const [warningDialogOpen, setWarningDialogOpen] = React.useState(false);
  const [description, setDescription] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [responsible, setResponsible] = React.useState("");
  const [dueDate, setDueDate] = React.useState<Date | null>(null);
  const [isTaskForOther, setIsTaskForOther] = React.useState(false);
  const [status, setStatus] = React.useState<TaskStatus>(TaskStatus.NotStarted);
  const [emailError, setEmailError] = React.useState<string>("");
  const [successSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const isEdit = Boolean(initialTask?.id);
  const invokerEmail = (appContext.state.invoker?.email || "").trim();
  const initialFormState = React.useMemo(() => {
    const initialActor = (initialTask?.actor || "").trim();
    const initialIsTaskForOther =
      Boolean(initialTask?.id) &&
      Boolean(initialActor) &&
      (!invokerEmail ||
        initialActor.toLowerCase() !== invokerEmail.toLowerCase());

    return {
      description: initialTask?.content || "",
      category: initialTask?.category || defaultCategory,
      dueDate: initialTask?.dueDate ? new Date(initialTask.dueDate) : null,
      status: initialTask?.status || TaskStatus.NotStarted,
      isTaskForOther: initialIsTaskForOther,
      responsible: initialIsTaskForOther ? initialActor : "",
    };
  }, [defaultCategory, initialTask, invokerEmail]);

  useEffect(() => {
    if (!open) return;
    setCategory(initialFormState.category);
    setDescription(initialFormState.description);
    setDueDate(initialFormState.dueDate);
    setStatus(initialFormState.status);
    setIsTaskForOther(initialFormState.isTaskForOther);
    setResponsible(initialFormState.responsible);
    setEmailError("");
  }, [open, initialFormState]);

  const canSave =
    Boolean((initialTask?.clientId || defaultClientId || "").trim()) &&
    Boolean(description.trim()) &&
    (!isTaskForOther || (Boolean(responsible.trim()) && !emailError));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);

    const selfActor = (
      appContext.state.invoker?.email ||
      defaultCreatorEmail ||
      initialTask?.creator ||
      defaultActorName ||
      ""
    ).trim();
    const resolvedActor = (
      isTaskForOther
        ? responsible
        : initialTask?.actor || selfActor
    ).trim();
    const resolvedCreator = (
      initialTask?.creator ||
      defaultCreatorEmail ||
      appContext.state.invoker?.email ||
      ""
    ).trim();

    const payload: Task = {
      id: initialTask?.id,
      clientId: (initialTask?.clientId || defaultClientId || "").trim(),
      sessionId: initialTask?.sessionId || defaultSessionId,
      sessionName: initialTask?.sessionName || defaultSessionName,
      content: description.trim(),
      category: category.trim() || "Coaching Hub",
      actor: resolvedActor || undefined,
      creator: resolvedCreator || undefined,
      status,
      dueDate: dueDate ? new Date(dueDate) : undefined,
    };

    try {
      const saved = await taskService.save(payload);
      if (isEdit) {
        onSaved?.(saved);
      } else {
        onCreated?.(saved);
      }
      onClose();
    } catch (err) {
      console.error("Failed to save task", err);
    } finally {
      setSaving(false);
      setSuccessSnackbarOpen(true);
    }
  };

  const hasChanges = React.useMemo(() => {
    const initialDueDateValue = initialFormState.dueDate
      ? dayjs(initialFormState.dueDate).startOf("day").valueOf()
      : null;
    const currentDueDateValue = dueDate
      ? dayjs(dueDate).startOf("day").valueOf()
      : null;
    const normalizedResponsible = isTaskForOther ? responsible.trim() : "";
    const initialResponsible = initialFormState.isTaskForOther
      ? initialFormState.responsible.trim()
      : "";

    return (
      description.trim() !== initialFormState.description.trim() ||
      category.trim() !== initialFormState.category.trim() ||
      status !== initialFormState.status ||
      isTaskForOther !== initialFormState.isTaskForOther ||
      normalizedResponsible !== initialResponsible ||
      currentDueDateValue !== initialDueDateValue
    );
  }, [
    category,
    description,
    dueDate,
    initialFormState,
    isTaskForOther,
    responsible,
    status,
  ]);

  const resetForm = React.useCallback(() => {
    setCategory(initialFormState.category);
    setDescription(initialFormState.description);
    setResponsible(initialFormState.responsible);
    setDueDate(initialFormState.dueDate);
    setStatus(initialFormState.status);
    setIsTaskForOther(initialFormState.isTaskForOther);
    setEmailError("");
  }, [initialFormState]);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      onClose();
      resetForm();
    }
  }, [hasChanges, onClose, resetForm]);

  const handleResponsibleChange = (value: string) => {
    setResponsible(value);
    setEmailError("");

    // Only validate when task is for another person
    if (!isTaskForOther) {
      return;
    }

    const trimmedValue = value.trim();

    if (trimmedValue === "") {
      return;
    }

    if (trimmedValue.includes("@")) {
      if (!isValidEmail(trimmedValue)) {
        setEmailError(t("quicktask.invalid_email"));
        return;
      }
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsTaskForOther(checked);
    if (!checked) {
      setResponsible("");
      setEmailError("");
    }
  };

  // Email validation functions taken from itemsPage
  const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleWarningClose}
        fullWidth
        maxWidth="md"
        keepMounted={false}
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title" slotProps={{ paper: {
          style: {
            borderRadius: "8px",
            backgroundColor: "white",
          },
        } }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <DialogTitle sx={{ textAlign: "left" }}>{t("quicktask.title")}</DialogTitle>

          <IconButton
            aria-label={t("button.close")}
            onClick={handleWarningClose}
            sx={{
              position: "absolute",
              right: 12,
              color: "#5F6368",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent dividers>
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {/* task description */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                variant="subtitle2"
                component="label"
                htmlFor="description-input"
              >
                {t("quicktask.description_title")} *
              </Typography>
              <TextField
                id="description-input"
                required
                placeholder={t("quicktask.description_placeholder")}
                multiline
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "black",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "black",
                    opacity: 1,
                  },
                }}
              />
            </Box>
            {/* task responsible */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="label"
                  htmlFor="responsible-input"
                >
                  {t("quicktask.responsible_title")} *
                </Typography>
                <FreeSingleSelect
                  onValueSelected={handleResponsibleChange}
                  options={[]}
                  value={
                    isTaskForOther
                      ? responsible
                      : appContext.state.invoker?.email || ""
                  }
                  placeholder={t("quicktask.responsible_placeholder")}
                  disabled={!isTaskForOther}
                  error={!!emailError}
                  hideTooltip={true}
                  maxVisibleItems={5}
                  useEmailSuggestions={true}
                  enableDelete={true}
                  enableUsageSort={true}
                  dropdownMinWidth={340}
                />
                {emailError && (
                  <Alert
                    severity="error"
                    sx={{
                      padding: "4px 8px",
                      fontSize: "0.875rem",
                      wordBreak: "break-all",
                    }}
                    role="alert"
                  >
                    {emailError}
                  </Alert>
                )}
              </Box>
              {/* task due date */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" component="label">
                  {t("quicktask.dueDate_title")}
                </Typography>
                <DatePicker
                  className="lighter-textfield"
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "black",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "black",
                      opacity: 1,
                    },
                  }}
                  value={dueDate ? dayjs(dueDate) : null}
                  onChange={(newDate) =>
                    setDueDate(newDate ? newDate.toDate() : null)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      "aria-label": t("quicktask.dueDate_title"),
                    },
                  }}
                />
              </Box>
              {/* task status */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="label"
                  htmlFor="status-select"
                >
                  {t("quicktask.status_title")}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    id="status-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TaskStatus)}
                    sx={{
                      "& .MuiSelect-select": {
                        fontFamily: "inherit",
                        fontSize: "1rem",
                        color: "black",
                      },
                    }}
                    MenuProps={{ slotProps: { paper: {
                        sx: {
                          backgroundColor: "white",
                          "& .MuiMenuItem-root": {
                            fontSize: "1rem",
                            fontFamily: "inherit",
                          },
                        },
                      } } }}
                  >
                    <MenuItem value={TaskStatus.NotStarted}>
                      {t(`item_status.${TaskStatus.NotStarted}`)}
                    </MenuItem>
                    <MenuItem value={TaskStatus.InProgress}>
                      {t(`item_status.${TaskStatus.InProgress}`)}
                    </MenuItem>
                    <MenuItem value={TaskStatus.OnHold}>
                      {t(`item_status.${TaskStatus.OnHold}`)}
                    </MenuItem>
                    <MenuItem value={TaskStatus.Completed}>
                      {t(`item_status.${TaskStatus.Completed}`)}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, display: "flex", alignItems: "center" }}
        >
          <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
            <Tooltip title={isActor ? t("quicktask.switch_disabled") : ""}>
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTaskForOther}
                      onChange={handleSwitchChange}
                      disabled={isActor} slotProps={{ input: { "aria-describedby": "switch-helper" } }}
                    />
                  }
                  label={t("quicktask.switch_placeholder")}
                />
              </span>
            </Tooltip>
          </Box>
          <Tooltip
            title={!canSave ? t("quicktask.fill_mandatory_fields") : ""}
          >
            <span>
              <Button
                variant="contained"
                autoFocus
                onClick={handleSave}
                disabled={!canSave || saving}
              >
                {t("button.save")}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          resetForm();
          onClose();
        }}
      />
      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
          icon={<CheckIcon fontSize="inherit" />}
        >
          {t("quicktask.success_message")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTask;
